import { Injectable } from '@angular/core';
import { X2jOptions, XMLParser } from 'fast-xml-parser';

import { HierarchyItem, OivModel, OivTree } from '../model/oiv-tree.model';
import { MappingOivType, OIV_TYPE, SECTION_TYPE, XML_TAG } from '../model/oiv-type.model';

@Injectable({
    providedIn: 'root',
})
export class OivXmlParserService {
    readonly #_oivAttrChapterName = 'oivAttr_chapter-name';

    oivTree: OivTree = new OivTree({
        tag: 'root',
        text: '',
        oivAttr_eID: 'root',
    });

    #fxp: XMLParser;
    #parsedObjects: any[] = [];
    #hierarchyList: HierarchyItem[] = [];
    #isDeckblatt = false;
    #isKi = false;

    constructor() {
        const defaultConfig: X2jOptions = {
            preserveOrder: true,
            ignoreAttributes: false,
            ignoreDeclaration: true,
            allowBooleanAttributes: true,
            attributeNamePrefix: 'oivAttr_',
            stopNodes: [
                '*.paragraph',
                '*.paragraph_l2',
                '*.paragraph_l3',
                '*.paragraph_l4',
                '*.paragraph_l5',
                '*.paragraph_l6',
                '*.paragraph_l7',
                '*.paragraph_l8',
                '*.paragraph_l9',
                '*.subtitle',
                '*.head',
                '*.title',
                '*.info',
            ],
            transformTagName: (tagName: string) => (tagName === 'chapter' ? 'chapter_group' : tagName),
            textNodeName: 'text',
        };
        this.#fxp = new XMLParser(defaultConfig);
    }

    getAllChapters(): OivModel[] {
        return this.oivTree.searchNodeByType(OIV_TYPE.CHAPTER);
    }

    parseOivXml(inputXml: string): any {
        inputXml = this.#escapeSelfClosingTag(inputXml);
        inputXml = this.#removeBreakElement(inputXml);
        inputXml = this.#escapeHtmlEntities(inputXml);
        this.#parsedObjects = this.#fxp.parse(inputXml);
        this.#buildOivTree(this.#parsedObjects);
        return this.#parsedObjects;
    }

    #buildOivTree(parsedObjects = this.#parsedObjects) {
        if (!parsedObjects) {
            return;
        }

        this.oivTree = new OivTree({
            tag: 'root',
            text: '',
            oivAttr_eID: 'root',
        });

        for (const obj of parsedObjects) {
            this.#buildOivTreeRecursive(obj, this.oivTree.root);
        }
    }

    /**
     * Recursively builds the OIV tree structure from the parsed XML objects.
     *
     * This method iterates through each key of the current parsed object.
     * If the value associated with a key is an array, it handles the array values by calling `#handleArrayValues`.
     * If the value is another object, it recursively calls itself to further build the tree structure from that object.
     *
     * With each key,
     * we get the current node that contains all the child nodes then we will for loop through the child nodes
     * to build the tree structure (horizontal traversal).
     *
     * Example: {
     *     "chapter_group": [
     *         {
     *             "section": [
     *                 {
     *                     "title": [
     *                         {
     *                             "text": "Kundeninformationen"
     *                         }
     *                     ]
     *                 }
     *             ]
     *         },
     *         {
     *             "info": [
     *                 {
     *                     "text": "Ausgabe 08.2024"
     *                 }
     *             ]
     *         },
     *         {
     *             "section_l2": [
     *                 {
     *                     "subtitle": [
     *                         {
     *                             "text": "Wer sind wir?"
     *                         }
     *                     ]
     *                 }
     *             ]
     *         },
     *         {
     *             "section_l2": [
     *                 {
     *                     "subtitle": [
     *                         {
     *                             "text": "Was ist versichert?"
     *                         }
     *                     ]
     *                 }
     *             ]
     *         }
     *     ]
     * }
     *
     */
    #buildOivTreeRecursive(currentParsedObject: any, parentNode: OivModel | null, chapterGroupName = '') {
        if (!currentParsedObject || !parentNode) {
            return;
        }

        // Go through all the keys in the current object
        for (const key in currentParsedObject) {
            if (typeof currentParsedObject[key] === 'object') {
                if (Array.isArray(currentParsedObject[key])) {
                    this.#handleArrayValues(key, currentParsedObject, parentNode, chapterGroupName);
                } else {
                    this.#buildOivTreeRecursive(currentParsedObject[key], parentNode);
                }
            }
        }
    }

    #handleArrayValues(key: string, parsedObject: any, parentNode: OivModel, chapterGroupName = '') {
        // For the key 'chapter_group'
        if (key === 'chapter_group') {
            const info = parsedObject[':@'];

            // If the chapter is KI, we need to set the isKi flag to true
            // Using for some specific logic in the #enrichNodeAttribute method
            this.#isKi = info[this.#_oivAttrChapterName] === 'ki';
            this.#isDeckblatt = info[this.#_oivAttrChapterName] === 'deckblatt';
            chapterGroupName = info[this.#_oivAttrChapterName];

            // We add the chapter's hierarchyItem to the hierarchyList
            // when we're in the chapter node then we can use it to build the hierarchy on its sections
            if (this.#hierarchyList.length === 0) {
                const chapterHierarchyItem: HierarchyItem = {
                    eId: info['oivAttr_eID'],
                    title: chapterGroupName,
                };
                this.#hierarchyList.push(chapterHierarchyItem);
            }
        }

        // Select every child node of the current node (the horizontal traversal)
        for (const child of parsedObject[key]) {
            // If the child is a leaf node, we get text value then assign it to the parent node
            // Base on the structure; the title of the chapter (parent node) is a leaf node
            if (child?.text) {
                parentNode!.text = child.text;
                continue;
            }

            const childNode = this.#createChildNode(child, parentNode, chapterGroupName);

            // If the child node is a chapter, container, or topic,
            // we pop the last insert hierarchyItem (add when #createChildNode the current node) out of the hierarchyList
            // It makes sure the hierarchyList only contains the hierarchy of the parent node
            if (
                key === 'chapter_group' ||
                (childNode.oivType &&
                    [OIV_TYPE.CHAPTER, OIV_TYPE.CONTAINER, OIV_TYPE.TOPIC].includes(childNode.oivType))
            ) {
                this.#hierarchyList.pop();
            }
        }
    }

    #createChildNode(currentParsedNode: any, parentOivNode: OivModel, chapterGroupName = '') {
        const nodeTagName = Object.keys(currentParsedNode).filter(k => k !== ':@')[0];
        const childOivNode = this.oivTree?.addChildToNode(parentOivNode!, {
            tag: nodeTagName,
            text: currentParsedNode?.text || '',
            oivAttr_eID: currentParsedNode[':@']?.oivAttr_eID || '',
            chapter_group_name: chapterGroupName,
            ...currentParsedNode[':@'],
        });

        // Mapping OIV_TYPE
        // if the tag name is a key in MappingOivType, assign the corresponding value to the oivType attribute
        // the tag name with leveling like: <subtitle_l'x'> (x is a number) will be treated as subtitle
        for (const [k, v] of Object.entries(MappingOivType)) {
            if (nodeTagName.includes(k)) {
                childOivNode.oivType = v;
                break;
            }
        }

        this.#enrichNodeAttribute(nodeTagName, parentOivNode, childOivNode);

        if (!this.#isDeckblatt) {
            // if the current processing node is a section or chapter_group,
            // we add the hierarchyItem to the hierarchyList
            // currentParsedNode is used to get the title because it contains all the child nodes of the current processing node.
            this.#handleHierarchy(nodeTagName, currentParsedNode, childOivNode, chapterGroupName);
        }

        this.#buildOivTreeRecursive(currentParsedNode, childOivNode, chapterGroupName);

        // ignore the num_group tag and merge the content of num_group with its parent
        this.#handleFlattenNumGroup(parentOivNode);
        return childOivNode;
    }

    #enrichNodeAttribute(childName: string, parentNode: OivModel, childNode: OivModel) {
        this.#enrichForSectionChildNode(childName, parentNode);
        this.#enrichForCommonChildNode(parentNode, childName, childNode);
        this.#enrichForBoxTitle(childName, parentNode, childNode);
    }

    #enrichForCommonChildNode(parentNode: OivModel, childName: string, childNode: OivModel) {
        if (parentNode.oivType === OIV_TYPE.CONTAINER_LIST && childName === XML_TAG.ITEM && this.#isKi) {
            childNode.oivType = OIV_TYPE.CONTAINER;
        }

        if ((childName === XML_TAG.NUMBERED || childName === XML_TAG.BULLET) && this.#isKi) {
            childNode.oivType = OIV_TYPE.CONTAINER_LIST;
        }

        if (
            childName === XML_TAG.PARA &&
            parentNode.tag === XML_TAG.ITEM &&
            parentNode.oivType !== OIV_TYPE.LIST_ITEM
        ) {
            childNode.oivType = OIV_TYPE.SECTION_TITLE;
        }
    }

    #enrichForSectionChildNode(childName: string, parentNode: OivModel) {
        const isSectionChildNode =
            childName.includes(XML_TAG.SUBTITLE) ||
            childName.includes(XML_TAG.TITLE) ||
            childName.includes(XML_TAG.HEAD);
        // If the child is a section child node (an element is <title>, <head> and <subtitle_l'x'>, x is a number),
        // assign oivType for the parent node to the corresponding type by tag name
        if (isSectionChildNode && parentNode?.tag?.includes(XML_TAG.SECTION)) {
            parentNode!.oivType = SECTION_TYPE[childName as keyof typeof SECTION_TYPE] || OIV_TYPE.CONTAINER;
        }
    }

    #enrichForBoxTitle(childName: string, parentNode: OivModel | null, childNode: OivModel | null) {
        if (childName.includes(XML_TAG.SUBTITLE) && parentNode?.oivType === OIV_TYPE.BOX) {
            childNode!.oivType = OIV_TYPE.BOX_TITLE;
        }
    }

    #handleFlattenNumGroup(parentNode: OivModel) {
        parentNode.content.forEach((content, index) => {
            if (content.oivType === OIV_TYPE.NUM_GROUP) {
                parentNode.content.splice(index, 1, ...content.content);
            }
        });
    }

    #escapeSelfClosingTag(text: string): string {
        // eslint-disable-next-line sonarjs/slow-regex
        return text.replace(/<(?!col\b|cell\b)[^>]+\/\s*>/g, '');
    }

    // Removes the content-bearing form <break attr="...">content</break>, including its content.
    // The self-closing form <break/> is already removed by #escapeSelfClosingTag, which must run first.
    #removeBreakElement(text: string): string {
        // eslint-disable-next-line sonarjs/slow-regex
        return text.replace(/<break(?:\s[^>]*)?>[\s\S]*?<\/break\s*>/g, '');
    }

    #escapeHtmlEntities(text: string): string {
        const e = document.createElement('textarea');
        e.innerHTML = text;
        // handle the case of empty input
        const input = e.childNodes.length === 0 ? '' : e.textContent;
        return input || '';
    }

    #extractNumber(sectionString: string): number {
        const match = sectionString.match(/section_l(\d+)/);
        if (match && match[1]) {
            return parseInt(match[1], 10);
        }
        return 0;
    }

    #handleHierarchy(nodeTagName: string, currentParsedNode: any, childNode: OivModel, chapterGroupName: string) {
        // Firstly, we need to add the hierarchyList to the current child node
        // It's because the current node should have the hierarchy of its parent node
        childNode!.hierarchy = [...this.#hierarchyList];

        // Secondly, we need to add the hierarchyItem of the current node to the hierarchyList
        // It will be used for the next child node (if exists)
        const { title, numbering } = this.#getTitleFromNode(nodeTagName, currentParsedNode, childNode);
        if (nodeTagName.includes('chapter_group') || nodeTagName.includes(XML_TAG.SECTION)) {
            this.#hierarchyList.push({
                eId: childNode?.oivAttr_eID as string,
                title: title,
                attributeOrder: numbering,
            });
        }
    }

    #getTitleFromNode(
        nodeTagName: string,
        currentParsedNode: any,
        childNode: OivModel,
    ): { title: string; numbering?: string } {
        if (nodeTagName.includes('chapter_group')) {
            return {
                title: currentParsedNode[':@']['oivAttr_chapter-name'],
            };
        } else if (nodeTagName.includes(XML_TAG.SECTION)) {
            return this.#getTitleFromSection(nodeTagName, currentParsedNode);
        }
        return { title: '' };
    }
    #getTitleFromSection(nodeTagName: string, currentParsedNode: any): { title: string; numbering?: string } {
        if (nodeTagName === XML_TAG.SECTION) {
            const titleObject = (currentParsedNode[nodeTagName] as any[]).find(value => XML_TAG.TITLE in value);
            return {
                title: titleObject ? titleObject[XML_TAG.TITLE][0]['text'] : undefined,
            };
        } else {
            // We need to get the x level of section (section_l'x' => x is a number)
            // We use it for detect the subtitle of the section (will be different because of structure)
            const level = this.#extractNumber(nodeTagName);
            return this.#determineSectionTitleByIndex(level, currentParsedNode, nodeTagName);
        }
    }

    #getTitleObject(tagName: string, parsedNode: any, nodeTagName: string) {
        return (parsedNode[nodeTagName] as any[]).find(value => tagName in value);
    }

    #getTitleAndNumbering(titleObject: any, tagName: string) {
        return {
            title: titleObject ? titleObject[tagName][0]['text'] : undefined,
            numbering: titleObject ? titleObject[':@']['oivAttr_chapter-full'] : undefined,
        };
    }

    #determineSectionTitleByIndex(
        index: number,
        currentParsedNode: any,
        nodeTagName: string,
    ): { title: string; numbering?: string } {
        try {
            // Ki and section_l3 have title in the subtitle tag
            if (this.#isKi || index === 3) {
                const titleObject = this.#getTitleObject(XML_TAG.SUBTITLE, currentParsedNode, nodeTagName);
                return this.#getTitleAndNumbering(titleObject, XML_TAG.SUBTITLE);
            } else if (index === 2) {
                // section_l2 has title in the head tag
                const titleObject = this.#getTitleObject(XML_TAG.HEAD, currentParsedNode, nodeTagName);
                return this.#getTitleAndNumbering(titleObject, XML_TAG.HEAD);
            } else if (index > 3) {
                // section_l4, section_l5, ... has title in the subtitle_l'x' tag (index minus 2)
                // example: section_l3 has subtitle tag
                //          section_l4 has subtitle_l2 tag
                const titleObject = this.#getTitleObject(`subtitle_l${index - 2}`, currentParsedNode, nodeTagName);
                return this.#getTitleAndNumbering(titleObject, `subtitle_l${index - 2}`);
            }
        } catch (error) {
            console.log('tag', nodeTagName, 'currentParsedNode', currentParsedNode);
            console.log('Error determining section title:', error);
        }
        return { title: '' };
    }
}
